import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  OverlayDrawer,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerBody,
  Text,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { BasicButton } from '@components/common/BasicButton';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { selectAllNotifications, dismissNotification } from '@store/slices/notificationsSlice';
import { NotificationTile } from './NotificationTile';
import { useNotificationsDrawerStyles } from './NotificationsDrawer.styles';

export interface NotificationsDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NotificationsDrawer({
  open,
  onOpenChange,
}: NotificationsDrawerProps): React.JSX.Element {
  const { t } = useTranslation('header');
  const styles = useNotificationsDrawerStyles();
  const dispatch = useAppDispatch();
  const items = useAppSelector(selectAllNotifications);
  const [exiting, setExiting] = useState<Set<string>>(new Set());

  const handleDismiss = useCallback((id: string) => {
    setExiting((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const handleExited = useCallback(
    (id: string) => {
      dispatch(dismissNotification(id));
      setExiting((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    },
    [dispatch]
  );

  return (
    <OverlayDrawer
      open={open}
      onOpenChange={(_, data) => onOpenChange(data.open)}
      position="end"
      size="small"
      data-testid="notifications-drawer"
    >
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <BasicButton
              variant="subtle"
              aria-label={t('common.close', { defaultValue: 'Close' })}
              icon={<Dismiss24Regular />}
              onClick={() => onOpenChange(false)}
              data-testid="notifications-drawer-close"
            />
          }
        >
          {t('notifications.label')}
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody className={styles.body}>
        {items.length === 0 ? (
          <Text className={styles.empty} data-testid="notifications-empty">
            {t('notifications.empty')}
          </Text>
        ) : (
          items.map((item) => (
            <NotificationTile
              key={item.id}
              notification={item}
              onDismiss={handleDismiss}
              onExited={handleExited}
              isExiting={exiting.has(item.id)}
            />
          ))
        )}
      </DrawerBody>
    </OverlayDrawer>
  );
}

NotificationsDrawer.displayName = 'NotificationsDrawer';
